import React from 'react';

const NAVY = '#1a2332';
const NAVY_LIGHT = '#2d3748';
const GREEN = '#16a34a';
const RED = '#ef4444';
const CYAN = '#01bcd4';

export type PipelineCardStatus = 'pending' | 'running' | 'succeeded' | 'failed';

function formatCost(cost: number | undefined): string {
  if (cost == null || cost === 0) return '';
  if (cost < 0.01) return '<$0.01';
  return `$${cost.toFixed(2)}`;
}

/** The stage card used by every pipeline sidebar: course, lesson, and render
    targets. One implementation so all pipelines look and behave identically. */
export function PipelineCard({
  stageId, number, name, status, selected, cost, runCount = 0, statusText, onClick,
}: {
  stageId: string;
  number: string;
  name: string;
  status: PipelineCardStatus;
  selected: boolean;
  cost?: number;
  runCount?: number;
  statusText?: string;
  onClick: () => void;
}) {
  const isCompleted = status === 'succeeded';
  const isFailed = status === 'failed';
  const isRunning = status === 'running';
  const cardBorder = selected ? CYAN : isCompleted ? GREEN : isFailed ? RED : NAVY_LIGHT;

  return (
    <div className="relative pl-8 mb-3">
      <div
        className="absolute left-[11px] top-5 w-[10px] h-[10px] rounded-full z-10 border-2"
        style={{
          backgroundColor: selected ? CYAN : isCompleted ? GREEN : isRunning ? NAVY : isFailed ? RED : NAVY_LIGHT,
          borderColor: '#f5f5f7',
        }}
      />
      <div
        className={`relative rounded-xl bg-white p-3 cursor-pointer transition-all duration-150 max-w-[260px]
          ${selected ? 'shadow-lg' : 'hover:shadow-md'}
        `}
        style={{ borderWidth: '2px', borderStyle: 'solid', borderColor: cardBorder }}
        onClick={onClick}
      >
        <div className="flex items-center gap-2">
          <span
            className="text-[10px] font-mono font-bold uppercase"
            style={{ color: isCompleted ? GREEN : isRunning ? NAVY : '#94a3b8' }}
          >{number}</span>
          <span
            className="text-[13px] font-semibold leading-tight"
            style={{ color: selected ? '#0e7490' : isCompleted ? '#374151' : isRunning ? NAVY : '#64748b' }}
          >{name}</span>
        </div>
        <div
          className="mt-1 text-[11px] flex items-center justify-between gap-2"
          style={{ color: isCompleted ? GREEN : isFailed ? RED : isRunning ? NAVY : '#9ca3af' }}
        >
          <span>{statusText ?? (isCompleted ? 'Completed' : isFailed ? 'Failed' : isRunning ? 'Processing...' : 'Pending')}</span>
          {cost !== undefined && cost > 0 && (
            <span
              className="text-[10px] font-mono text-gray-500"
              title={runCount > 1 ? `${runCount} runs total ${formatCost(cost)}` : formatCost(cost)}
            >
              {formatCost(cost)}{runCount > 1 ? ` x${runCount}` : ''}
            </span>
          )}
        </div>
        {isRunning ? (
          <div className="mt-2 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: '#e5e7eb', marginRight: '50px' }}>
            <div
              className="h-full w-1/3 rounded-full"
              style={{
                background: `linear-gradient(90deg, ${NAVY}, ${NAVY_LIGHT}, ${NAVY})`,
                animation: 'shimmer 1.5s ease-in-out infinite',
              }}
            />
          </div>
        ) : (
          <div className="mt-1 text-[10px] font-mono text-gray-300">{stageId}</div>
        )}
      </div>
    </div>
  );
}
